import type { Bounds, Point, Polygon, Manifest } from './contracts.ts';

export type Vertex = [number, number, number];
export interface WalkSurface {
  id: string;
  kind: 'sidewalk' | 'crossing' | 'bridge-deck' | 'ramp';
  segmentId: string;
  vertices: Vertex[];
  triangles: [number, number, number][];
}
export interface CorridorAsset {
  id: string; url: string; bounds: Bounds; textureCount: number;
  kind: 'landmark' | 'facade' | 'walkway' | 'bridge';
}
export interface GroundOverride {
  tile_id:string;url:string;bounds:Bounds;object_name:string;mesh_name:string;
  replace_ground_only:true;textureCount:1;mask_source?:unknown;masked?:boolean;mask?:unknown;
}
export interface CorridorSource {
  id: string; title: string; author: string; sourceUrl: string;
  licenseName: string; licenseUrl: string; captureDate?: string; changes: string;
}
export interface CorridorPhoto { id: string; url: string; sourceId: string; caption: string }
export interface CorridorWaypoint {
  id: string; name: string; x: number; z: number; y?: number;
  headingRadians: number; description?: string; photoIds: string[];
  surfaceId?:string;positionToleranceMetres?:number;
}
export interface CorridorConfig {
  schemaVersion: 1; id: string; name: string; baselineManifestSha256: string;
  origin: Manifest['origin']; lengthMetres: number; route: Point[];
  waypoints: CorridorWaypoint[];
  segments: { id: string; bounds: Bounds; assetIds: string[]; surfaceIds: string[] }[];
  assets: CorridorAsset[]; surfaces: WalkSurface[];
  groundOverrides?:GroundOverride[];
  barriers?: (Polygon & { id: string; minY: number; maxY: number })[];
  photos: CorridorPhoto[]; sources: CorridorSource[];
}

export function corridorResource(relative: string, base: URL, containmentRoot=base): URL {
  if (typeof relative!=='string' || !relative || /^[a-z]+:|^[/\\]|[\s\\\u0000-\u001f]|%2f|%5c|%25/i.test(relative)) throw new Error('Corridor resources must be local relative URLs');
  return corridorLoadingResource(relative,base,containmentRoot);
}

/** GLTFLoader passes resolved absolute URLs to its asset-local manager. */
export function corridorLoadingResource(value:string,base:URL,containmentRoot=base):URL {
  if(typeof value!=='string' || !value || /[\s\\\u0000-\u001f]|%2f|%5c|%25/i.test(value))throw new Error('A corridor resource has an unsafe URL encoding');
  const url = new URL(value, base);
  if (url.origin !== containmentRoot.origin || !url.pathname.startsWith(containmentRoot.pathname) || url.search || url.hash) throw new Error('A corridor resource leaves its model folder');
  return url;
}

export function validCorridor(config: CorridorConfig, baseline: Manifest): void {
  if (config.schemaVersion !== 1 || !config.id || !config.waypoints?.length || config.route?.length < 2 || !config.surfaces?.length) throw new Error('Corridor route is unavailable');
  if(!Number.isFinite(config.lengthMetres) || config.lengthMetres<0)throw new Error('Corridor route length is invalid');
  for (const key of ['easting','northing','elevationDatum'] as const) if (config.origin?.[key] !== baseline.origin[key]) throw new Error('Corridor and city coordinates differ');
  if (config.origin.crs !== baseline.origin.crs) throw new Error('Corridor coordinate system differs');
  const finite = (values: number[]) => values.every(Number.isFinite);
  if (!config.route.every(point => point.length === 2 && finite(point)) || !config.waypoints.every(point => finite([point.x, point.z, point.headingRadians]))) throw new Error('Corridor coordinates are invalid');
  if(config.waypoints.some(point=>point.y!==undefined && !Number.isFinite(point.y) || point.positionToleranceMetres!==undefined && (!Number.isFinite(point.positionToleranceMetres) || point.positionToleranceMetres<0) || point.surfaceId!==undefined && typeof point.surfaceId!=='string'))throw new Error('A tour station constraint is invalid');
  const ids = new Set<string>();
  for (const surface of config.surfaces) {
    if (ids.has(surface.id) || !surface.vertices.length || !surface.triangles.length || !['sidewalk','crossing','bridge-deck','ramp'].includes(surface.kind)) throw new Error('Corridor surfaces are invalid');
    ids.add(surface.id);
    if (!surface.vertices.every(point => point.length === 3 && finite(point)) || !surface.triangles.every(triangle => triangle.length === 3 && triangle.every(index => Number.isInteger(index) && index >= 0 && index < surface.vertices.length))) throw new Error('Corridor triangles are invalid');
  }
  for (const asset of config.assets) if (!Number.isInteger(asset.textureCount) || asset.textureCount < 0 || asset.textureCount > 2) throw new Error('A corridor model exceeds its photo budget');
  if(config.waypoints.some(point=>point.surfaceId && !ids.has(point.surfaceId)))throw new Error('A tour station refers to an unknown walking surface');
  const sourceIds=new Set(config.sources.map(source=>source.id)),photoIds=new Set(config.photos.map(photo=>photo.id));
  if(config.photos.some(photo=>!sourceIds.has(photo.sourceId)) || config.waypoints.some(point=>point.photoIds.some(id=>!photoIds.has(id)))) throw new Error('A tour photograph has no verified source');
}

/** Match constructor resource checks before any graphics allocation. */
export function validCorridorResources(config:CorridorConfig,baseline:Manifest,modelUrl:URL,origin:string):void {
  validCorridor(config,baseline);
  if(!modelUrl.pathname.endsWith('/') || modelUrl.search || modelUrl.hash || modelUrl.origin!==origin)throw new Error('The corridor model needs a same-origin folder URL');
  const boundsValid=(bounds:Bounds)=>bounds && [bounds.minX,bounds.maxX,bounds.minZ,bounds.maxZ].every(Number.isFinite) && bounds.minX<=bounds.maxX && bounds.minZ<=bounds.maxZ;
  const assetIds=new Set<string>();
  for(const asset of config.assets) {
    if(!asset.id || assetIds.has(asset.id) || !boundsValid(asset.bounds))throw new Error('A corridor asset is invalid');assetIds.add(asset.id);
    if(!corridorResource(asset.url,modelUrl).pathname.toLowerCase().endsWith('.gltf'))throw new Error('Corridor models require external glTF');
  }
  for(const photo of config.photos)corridorResource(photo.url,modelUrl);
  const tileIds=new Set<string>();
  for(const override of config.groundOverrides??[]) {
    const tile=baseline.tiles.find(tile=>tile.id===override.tile_id);
    if(!tile || tileIds.has(override.tile_id) || override.replace_ground_only!==true || override.textureCount!==1 || !boundsValid(override.bounds))throw new Error('A corridor ground override is invalid');tileIds.add(override.tile_id);
    for(const key of ['minX','maxX','minZ','maxZ'] as const)if(Math.abs(override.bounds[key]-tile.bounds[key])>.001)throw new Error('A ground override has different tile bounds');
    if(!corridorResource(override.url,modelUrl).pathname.toLowerCase().endsWith('.gltf'))throw new Error('Ground overrides require external glTF');
  }
}
